import React, { useEffect, useState } from "react";
import { Landmark, CalendarCheck, Wallet, PencilLine, FileDown, Loader2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import api, { formatZAR, formatApiErrorDetail } from "../lib/api";

export default function PayoutPanel() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [statements, setStatements] = useState([]);
  const [downloading, setDownloading] = useState(null);

  const load = () => {
    api.get("/owner/payouts").then((r) => setData(r.data)).catch(() => {});
    api.get("/owner/statements").then((r) => setStatements(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const download = async (month) => {
    setDownloading(month);
    try {
      const res = await api.get(`/owner/statements/${month}.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `tadada-statement-${month}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error("Could not generate the statement");
    } finally {
      setDownloading(null);
    }
  };

  if (!data) return <div className="mt-6 h-40 rounded-2xl bg-[#F7F8FA] animate-pulse" />;

  return (
    <div className="mt-8 space-y-6" data-testid="payout-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Kpi icon={<CalendarCheck size={18} />} label="Next payout" value={formatZAR(data.pending_total)} sub={data.upcoming[0] ? `Friday ${format(parseISO(data.upcoming[0].cycle_date), "d MMM")}` : "Nothing due yet"} testid="payout-kpi-pending" />
        <Kpi icon={<Wallet size={18} />} label="Trips in progress" value={formatZAR(data.in_progress_total)} sub="Payable once returned" testid="payout-kpi-progress" />
        <Kpi icon={<Landmark size={18} />} label="Paid out to date" value={formatZAR(data.paid_total)} sub={`${data.paid.length} payout${data.paid.length === 1 ? "" : "s"}`} testid="payout-kpi-paid" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <div className="flex items-baseline justify-between">
            <h3 className="font-display text-lg font-bold text-[#0a1f4d]">Payout schedule</h3>
            <p className="text-xs text-[#64748B]">Weekly EFT every {data.cycle_day} · trips completed Sat–Fri</p>
          </div>

          {data.upcoming.length === 0 && data.paid.length === 0 && (
            <p className="text-sm text-[#64748B] mt-4">Completed trips appear here and are paid the following Friday.</p>
          )}

          <div className="mt-4 space-y-3">
            {data.upcoming.map((c) => (
              <Cycle key={c.cycle_date} cycle={c} />
            ))}
            {data.paid.map((p) => (
              <Cycle key={p.id} cycle={{ ...p, status: "paid" }} />
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="bank-details-card">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-bold text-[#0a1f4d]">Bank account</h3>
            {data.bank_details && !editing && (
              <button onClick={() => setEditing(true)} className="text-xs font-semibold text-[#1d7ffd] inline-flex items-center gap-1" data-testid="bank-edit-btn"><PencilLine size={12} /> Edit</button>
            )}
          </div>
          {!data.bank_details && !editing && (
            <div className="mt-3">
              <p className="text-sm text-[#B45309] bg-[#FFFBEB] border border-[#F59E0B]/40 rounded-xl p-3" data-testid="bank-missing-warning">Add your bank details so we can pay you. Payouts are held until this is complete.</p>
              <button onClick={() => setEditing(true)} className="btn-primary text-sm mt-3 w-full" data-testid="bank-add-btn">Add bank details</button>
            </div>
          )}
          {data.bank_details && !editing && (
            <dl className="mt-3 text-sm space-y-1.5" data-testid="bank-details-view">
              <Row k="Bank" v={data.bank_details.bank_name} />
              <Row k="Holder" v={data.bank_details.account_holder} />
              <Row k="Account" v={data.bank_details.account_number_masked} />
              <Row k="Branch" v={data.bank_details.branch_code} />
              <Row k="Type" v={data.bank_details.account_type} />
            </dl>
          )}
          {editing && <BankForm initial={data.bank_details} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}

          <div className="mt-6 pt-5 border-t border-[#EEF2F7]" data-testid="statements-card">
            <h3 className="font-display text-lg font-bold text-[#0a1f4d]">Monthly statements</h3>
            <p className="text-xs text-[#64748B] mt-1">PDF of trips, fees and payouts — ready for your accountant.</p>
            {statements.length === 0 && <p className="text-sm text-[#64748B] mt-3">Your first statement appears after a completed trip.</p>}
            <ul className="mt-3 space-y-2">
              {statements.map((m) => (
                <li key={m.month} className="flex items-center gap-3 text-sm" data-testid={`statement-${m.month}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#0a1f4d]">{format(parseISO(`${m.month}-01`), "MMMM yyyy")}</p>
                    <p className="text-[11px] text-[#64748B]">{m.trips} trip{m.trips === 1 ? "" : "s"} · net {formatZAR(m.net)} · paid {formatZAR(m.paid_out)}</p>
                  </div>
                  <button onClick={() => download(m.month)} disabled={downloading === m.month} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0a1f4d] border border-[#E2E8F0] rounded-full px-3 py-1.5 hover:border-[#0a1f4d] disabled:opacity-60" data-testid={`statement-download-${m.month}`}>
                    {downloading === m.month ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />} PDF
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function Cycle({ cycle }) {
  const [open, setOpen] = useState(false);
  const tone = { due: "bg-[#F59E0B]/15 text-[#B45309]", scheduled: "bg-[#1d7ffd]/10 text-[#1d7ffd]", paid: "bg-[#10B981]/15 text-[#065F46]" }[cycle.status];
  const label = { due: "Processing", scheduled: "Scheduled", paid: "Paid" }[cycle.status];
  return (
    <div className="rounded-xl border border-[#E2E8F0]" data-testid={`payout-cycle-${cycle.cycle_date}-${cycle.status}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-4 p-4 text-left">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#0a1f4d]">Friday {format(parseISO(cycle.cycle_date), "d MMM yyyy")}</p>
          <p className="text-xs text-[#64748B]">{(cycle.bookings || []).length} trip{(cycle.bookings || []).length === 1 ? "" : "s"}{cycle.paid_at ? ` · paid ${format(new Date(cycle.paid_at), "d MMM")}` : ""}{cycle.reference ? ` · ref ${cycle.reference}` : ""}</p>
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${tone}`}>{label}</span>
        <p className="font-display font-black text-[#0a1f4d] w-28 text-right">{formatZAR(cycle.amount)}</p>
      </button>
      {open && (
        <div className="border-t border-[#EEF2F7] px-4 py-3 space-y-2 text-xs">
          {(cycle.bookings || []).map((b) => (
            <div key={b.id} className="flex items-center gap-3">
              <span className="flex-1 truncate text-[#334155]">{b.vehicle_title} · {b.customer_name}</span>
              <span className="text-[#64748B]">{formatZAR(b.rental)}{b.delivery ? ` + ${formatZAR(b.delivery)} delivery` : ""}{b.late_fee ? ` + ${formatZAR(b.late_fee)} late` : ""} − {formatZAR(b.commission)} fee</span>
              <span className="font-bold text-[#0a1f4d] w-20 text-right">{formatZAR(b.payout)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BankForm({ initial, onCancel, onSaved }) {
  const [f, setF] = useState({ bank_name: "", account_holder: "", account_number: "", branch_code: "", account_type: "cheque", ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const { account_number_masked, updated_at, ...payload } = f;
      await api.put("/owner/bank-details", payload);
      toast.success("Bank details saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save bank details");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3 space-y-2.5" data-testid="bank-form">
      <input className="ipx" placeholder="Bank (e.g. FNB, Capitec)" value={f.bank_name} onChange={set("bank_name")} data-testid="bank-name" />
      <input className="ipx" placeholder="Account holder" value={f.account_holder} onChange={set("account_holder")} data-testid="bank-holder" />
      <input className="ipx" placeholder="Account number" inputMode="numeric" value={f.account_number} onChange={set("account_number")} data-testid="bank-account" />
      <div className="grid grid-cols-2 gap-2">
        <input className="ipx" placeholder="Branch code" inputMode="numeric" value={f.branch_code} onChange={set("branch_code")} data-testid="bank-branch" />
        <select className="ipx" value={f.account_type} onChange={set("account_type")} data-testid="bank-type">
          <option value="cheque">Cheque / current</option>
          <option value="savings">Savings</option>
          <option value="business">Business</option>
        </select>
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={onCancel} className="btn-outline !py-2.5 text-sm flex-1" data-testid="bank-cancel">Cancel</button>
        <button onClick={save} disabled={busy} className="btn-primary text-sm flex-1 disabled:opacity-60" data-testid="bank-save">{busy ? "Saving…" : "Save"}</button>
      </div>
      <style>{`.ipx{width:100%;background:#F7F8FA;border:1px solid #E2E8F0;border-radius:0.75rem;padding:10px 14px;font-size:14px;color:#0a1f4d;outline:none}.ipx:focus{border-color:#1d7ffd}`}</style>
    </div>
  );
}

const Kpi = ({ icon, label, value, sub, testid }) => (
  <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-[#64748B]"><span className="text-[#1d7ffd]">{icon}</span><p className="text-[10px] tracking-[0.16em] uppercase font-bold">{label}</p></div>
    <p className="font-display font-black mt-1 text-2xl text-[#0a1f4d]">{value}</p>
    <p className="text-[11px] text-[#64748B] mt-1">{sub}</p>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3"><dt className="text-[#64748B]">{k}</dt><dd className="font-semibold text-[#0a1f4d] capitalize">{v}</dd></div>
);
